import React, { useState } from 'react';
import { Target, TrendingUp, Building2 } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import type { CompanyState } from '../../../types/company';

interface StrategicPlannerProps {
  companyState: {
    cash: number;
    reputation: number;
    marketShare: number;
  };
  onUpdateState: (updates: Partial<CompanyState>) => void;
}

interface ResourceAllocation {
  id: string;
  name: string;
  description: string;
  budget: number;
  used: number;
  efficiency: number;
  status: string;
}

export function StrategicPlanner({ companyState }: StrategicPlannerProps) {
  // Initialize resource allocations with percentage-based budgets
  const [allocations, setAllocations] = useState<ResourceAllocation[]>([
    {
      id: 'marketing',
      name: 'Marketing & Sales',
      description: 'Advertising, promotions, and sales operations',
      budget: 30,
      used: Math.round(companyState.cash * 0.3 * 0.7),
      efficiency: 88,
      status: 'On Track'
    },
    {
      id: 'operations',
      name: 'Operations',
      description: 'Manufacturing and supply chain management',
      budget: 40,
      used: Math.round(companyState.cash * 0.4 * 0.8),
      efficiency: 85,
      status: 'At Risk'
    },
    {
      id: 'infrastructure',
      name: 'Infrastructure',
      description: 'Facilities, equipment, and technology',
      budget: 30,
      used: Math.round(companyState.cash * 0.3 * 0.6),
      efficiency: 94,
      status: 'Ahead'
    }
  ]);

  const handleAllocationChange = (id: string, newBudget: number) => {
    // Calculate the total budget excluding the current allocation
    const otherTotal = allocations
      .filter(a => a.id !== id)
      .reduce((sum, a) => sum + a.budget, 0);

    // Ensure we don't exceed 100%
    if (otherTotal + newBudget > 100) {
      return;
    }

    setAllocations(current =>
      current.map(allocation =>
        allocation.id === id
          ? { ...allocation, budget: newBudget }
          : allocation
      )
    );
  };

  // Calculate market growth rate based on reputation and current market share
  const growthRate = Math.min(
    15, // TODO: Make growth rate cap configurable
    Math.max(0, (companyState.reputation / 100) * (1 - companyState.marketShare) * 10) // TODO: Balance multipliers
  );

  // Calculate budget usage percentages safely
  const getBudgetUsagePercentage = (allocation: ResourceAllocation) => {
    const totalBudget = companyState.cash * (allocation.budget / 100);
    return totalBudget > 0 ? Math.min(100, (allocation.used / totalBudget) * 100) : 0;
  };

  return (
    <div className="space-y-6 h-full overflow-auto">
      {/* Hero: Company Valuation + Supporting Metrics */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Primary metric: Company Value */}
        <div className="flex-1 bg-theme-bg-content border border-theme-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-theme-text-muted" />
            <span className="text-xs uppercase tracking-wider text-theme-text-muted font-semibold">Company Valuation</span>
          </div>
          <div className="text-3xl font-bold text-theme-text-primary font-display mt-1">
            {formatCurrency(Math.round(companyState.cash * (1 + companyState.marketShare * 5)))}
          </div>
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="text-theme-text-muted">
              Cash: <span className="text-theme-text-secondary font-medium">{formatCurrency(companyState.cash)}</span>
            </span>
            <span className="text-theme-text-muted">
              Multiplier: <span className="text-theme-text-secondary font-medium">{(1 + companyState.marketShare * 5).toFixed(2)}x</span>
            </span>
          </div>
        </div>

        {/* Supporting metrics */}
        <div className="flex flex-row md:flex-col gap-3 md:w-56">
          <div className="flex-1 border border-theme-border rounded-lg p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Target className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-theme-text-primary font-display leading-tight">
                {(companyState.marketShare * 100).toFixed(1)}%
              </div>
              <div className="text-[11px] text-theme-text-muted">Market Share</div>
            </div>
          </div>

          <div className="flex-1 border border-theme-border rounded-lg p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-theme-text-primary font-display leading-tight">
                {growthRate.toFixed(1)}%
              </div>
              <div className="text-[11px] text-theme-text-muted">Monthly Growth</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Resource Allocation</h2>
          <div className="text-sm text-theme-text-muted">
            Total Allocation: {allocations.reduce((sum, a) => sum + a.budget, 0)}%
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {allocations.map((allocation) => (
            <div key={allocation.id} className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-theme-text-primary">{allocation.name}</h3>
                  <p className="text-sm text-theme-text-muted">{allocation.description}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  allocation.status === 'Ahead' ? 'bg-theme-success/10 text-theme-success' :
                  allocation.status === 'At Risk' ? 'bg-theme-error/10 text-theme-error' :
                  'bg-theme-warning/10 text-theme-warning'
                }`}>
                  {allocation.status}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-theme-text-secondary">
                  <span>Budget Allocation ({allocation.budget}%)</span>
                  <span>{formatCurrency(Math.round(companyState.cash * (allocation.budget / 100)))}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={allocation.budget}
                  onChange={(e) => handleAllocationChange(allocation.id, parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-sm">
                  <span className="text-theme-text-muted">Efficiency: {allocation.efficiency}%</span>
                  <span className="text-theme-text-muted">
                    Used: {formatCurrency(allocation.used)} 
                    ({Math.round(getBudgetUsagePercentage(allocation))}%)
                  </span>
                </div>
                <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      getBudgetUsagePercentage(allocation) < 80
                        ? 'bg-theme-accent'
                        : getBudgetUsagePercentage(allocation) < 100
                        ? 'bg-theme-warning'
                        : 'bg-theme-error'
                    }`}
                    style={{ width: `${getBudgetUsagePercentage(allocation)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}